import { Component, EventEmitter, inject, Input, NgZone, OnDestroy, OnInit, Output } from '@angular/core'
import { DotFlashingComponent, ResponsiveComponent } from "@nx-superprep/shared"
import { GameplayToolbarComponent, ToolbarType } from "../gameplay-toolbar"
import { FrontendCard as Card, Choice, StudyPhase, StudySetting } from '@nx-superprep/backend/firebase'
import { log } from '@nx-superprep/utils'

import { GameplayPlayerNGXComponent } from '../gameplay-player-ngx'
import { createGameplay, GameplayPlayerTSXComponent } from '../gameplay-player-tsx'
import { GameplayService } from '../gameply.service'

@Component({
  standalone: true,
  selector: 'gameplay-player',
  templateUrl: './gameplay-player.component.html',
  styleUrls: ['./gameplay-player.component.scss'],
  imports: [
    GameplayToolbarComponent,
    GameplayPlayerNGXComponent,
    GameplayPlayerTSXComponent,
    DotFlashingComponent,
  ],
})
export class GameplayPlayerComponent extends ResponsiveComponent implements OnInit, OnDestroy {
  @Input() toolbar: ToolbarType = {home: true, progress: true, debug:false, editor:false, setting: true}
  @Input() setting!: StudySetting
  @Input() phase!: StudyPhase
  @Input() progress = 0
  @Input() isAutoplay = false
  @Input() rightAnswerDisplayTime = 0
  @Input() wrongAnswerDisplayTime = 0

  private _didAnswer = false
  @Input()
  get didAnswer() {
    return this._didAnswer
  }
  set didAnswer(value) {
    this._didAnswer = value
  }

  private _card: Card | undefined = undefined
  @Input()
  get card() {
    return this._card
  }
  set card(value) {
    if (!value) { return }
    if (!this.didAnswer && value.id === this._card?.id) { return }
    Howler.stop()
    if (!Number.isNaN(Howler?.ctx?.currentTime||NaN)) { Howler.unload() }
    const _useTSX = this.useTSX && !!value && !!createGameplay(value.template)
    log.debug(`using ${_useTSX ? '%cReact' : '%cAngular'}`, _useTSX ? 'color:blue' : 'color:red', `for gameplay ${value?.gameplay.name}`)
    if (value) { value.gameplayStatus = _useTSX ? 'react' : 'angular' }
    this.useTSX = _useTSX
    if (!this.isWeb) { this.orientationService.lockOrientation(this.orientation(value)) }
    this.zone.run(() => this._card = value)
    // non-TSX components do not emit onPresentQuestion, so we need handle it here
    if (!this.useTSX) { this.onAction('onPresentQuestion') }
  }

  private _useTSX = true
  @Input()
  get useTSX() { return this._useTSX }
  set useTSX(value) {
    if (value === this._useTSX) { return }
    this.zone.run(() => this._useTSX = value)
    this.useTSXChange.emit(value)
  }

  @Output() useTSXChange = new EventEmitter<boolean>()
  @Output() selected = new EventEmitter<Choice>()
  @Output() action = new EventEmitter<string>()

  get hasAudio() {
    return !!(this.card?.question?.hasAudio || this.card?.answer?.hasAudio)
  }

  getChoice(isAnswer = false) {
    return this.card?.choices.find(c => isAnswer ? c.isAnswer : !c.isAnswer)
  }

  onAction(e: string) {
    const [action, selected] = e.split(' ')
    log.debug(`received action ${e}`)
    switch (action) {
      case 'answer': this.forceAnswer(selected === 'true'); break
      case 'complete': this.onToggleAutoplay(); break
      case 'onPresentQuestion': if (this.isAutoplay) { setTimeout(() => this.forceAnswer(true), 1000) }; break
      default: this.action.emit(e)
    }
  }

  forceAnswer(isAnswer = false) {
    this.onSelect(this.card?.template.getChoice(isAnswer));
  }

  onToggleAutoplay() {
    this.isAutoplay = !this.isAutoplay
    if (this.isAutoplay) { this.forceAnswer(true) }
  }

  onSelect(choice?: Choice): void {
    if (!choice) { return }
    if (this.setting.soundEffect){
      this.gameplayService.playSoundEffect(choice.isAnswer?'correct':'incorrect')
    }
    this.selected.emit(choice)
  }

  // orientation helpers

  orientation(card?: Card) {
    return (card && ['periodic', 'diagram'].includes(card?.template.gameplay?.type??'')) ? 'landscape-primary' : 'portrait-primary'
  }

  readonly zone = inject(NgZone)
  readonly gameplayService = inject(GameplayService)
  override ngOnInit() {
    super.ngOnInit();
    this.gameplayService.initializeSoundEffects(); // Initialize sound effects
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.gameplayService.cleanup(); // Clean up resources
  }
}
